import dayjs from 'dayjs';
import type { AtvModel } from '../hooks/useAtvModels';
import type { Accessory } from '../hooks/useAccessories';
import { calculatePrice, calculateDailyPrice, getDeposit } from '../helpers/ReservationForm';
import { getAccessoriesTotalPrice } from '../utils/accessoriesAvailability';
import { BOOKING_TIMEZONE, BookingSelection } from './model';

export interface CatalogPrice {
  source: 'api-catalog';
  rentalMinor: number;
  accessoriesMinor: number;
  totalMinor: number;
  refundableDepositMinor: number;
  payableNowMinor: number;
}

export type CatalogPriceResult =
  | { status: 'ready'; price: CatalogPrice }
  | { status: 'pending'; code: 'CATALOG_MISSING' }
  | { status: 'error'; code: 'INVALID_CATALOG' | 'INVALID_SELECTION' };

export type VehicleCatalogPrices =
  | {
    status: 'ready';
    threeHoursMinor: number;
    dayMinor: number;
    weekendMinor: number | null;
    weekMinor: number;
    depositHourMinor: number;
    depositDayMinor: number;
  }
  | { status: 'error'; code: 'INVALID_CATALOG' };

export function getVehicleCatalogPrices(vehicle: AtvModel): VehicleCatalogPrices {
  const amounts = [
    vehicle.pricePerHour * 3, vehicle.price, vehicle.weekendPrice,
    calculateDailyPrice(168, vehicle), vehicle.depositHour, vehicle.depositDay,
  ];
  const minor = amounts.map(amount => Math.round(amount * 100));
  if (!amounts.every(amount => Number.isFinite(amount) && amount >= 0) || !minor.every(Number.isSafeInteger)) {
    return { status: 'error', code: 'INVALID_CATALOG' };
  }
  return {
    status: 'ready',
    threeHoursMinor: minor[0], dayMinor: minor[1],
    weekendMinor: vehicle.weekendPrice > 0 ? minor[2] : null,
    weekMinor: minor[3], depositHourMinor: minor[4], depositDayMinor: minor[5],
  };
}

export function calculateCatalogPrice(
  selection: BookingSelection,
  vehicles: AtvModel[],
  accessories: Accessory[] | null,
): CatalogPriceResult {
  const vehicle = vehicles.find(item => item.id === selection.vehicleId);
  if (!vehicle || (selection.accessoryIds.length && !accessories)) return { status: 'pending', code: 'CATALOG_MISSING' };
  const from = dayjs(selection.startsAt).tz(BOOKING_TIMEZONE);
  const to = dayjs(selection.endsAt).tz(BOOKING_TIMEZONE);
  const hours = (to.valueOf() - from.valueOf()) / 3_600_000;
  if (!from.isValid() || !to.isValid()
    || (selection.offer === 'three-hours' && (hours !== 3 || selection.accessoryIds.length > 0))
    || (selection.offer === 'day' && hours < 3)
    || selection.offer === 'weekend'
    || new Set(selection.accessoryIds).size !== selection.accessoryIds.length) {
    return { status: 'error', code: 'INVALID_SELECTION' };
  }
  const selectedAccessories = (accessories || []).filter(item => selection.accessoryIds.includes(item.id));
  if (selectedAccessories.length !== selection.accessoryIds.length) return { status: 'error', code: 'INVALID_SELECTION' };
  const catalogAmounts = [
    vehicle.price, vehicle.pricePerHour, vehicle.weekPrice, vehicle.depositHour, vehicle.depositDay,
    ...selectedAccessories.flatMap(item => [item.price, item.deposit]),
  ];
  if (!catalogAmounts.every(value => Number.isFinite(value) && value >= 0)) return { status: 'error', code: 'INVALID_CATALOG' };
  const rental = selection.offer === 'day'
    ? calculateDailyPrice(hours, vehicle)
    : calculatePrice(from, to, from, to, vehicle.name, [vehicle]);
  const extras = getAccessoriesTotalPrice(selectedAccessories.map(item => item.plate), selectedAccessories) * Math.ceil(hours / 24);
  const deposit = (selection.offer === 'day' ? vehicle.depositDay : getDeposit(from, to, from, to, vehicle))
    + selectedAccessories.reduce((sum, item) => sum + item.deposit, 0);
  const values = [rental, extras, deposit];
  if (!values.every(value => Number.isFinite(value) && value >= 0)) return { status: 'error', code: 'INVALID_CATALOG' };
  const rentalMinor = Math.round(rental * 100);
  const accessoriesMinor = Math.round(extras * 100);
  const totalMinor = rentalMinor + accessoriesMinor;
  const refundableDepositMinor = Math.round(deposit * 100);
  // Preserve the legacy 30% advance rounded to PLN 50, never exceeding the total.
  const payableNowMinor = selection.paymentType === 'full'
    ? totalMinor : Math.min(totalMinor, Math.ceil(totalMinor * 0.3 / 5000) * 5000);
  if (![rentalMinor, accessoriesMinor, totalMinor, refundableDepositMinor, payableNowMinor].every(Number.isSafeInteger)) {
    return { status: 'error', code: 'INVALID_CATALOG' };
  }
  return {
    status: 'ready',
    price: { source: 'api-catalog', rentalMinor, accessoriesMinor, totalMinor, refundableDepositMinor, payableNowMinor },
  };
}
